import {
  CSSResult, html, LitElement, PropertyValues, unsafeCSS,
} from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import heartFilledNormal from '@breuni-ui/nui/dist/design-tokens/images/icons/nui-header_heart_filled_size=18.svg';
import heartNormal from '@breuni-ui/nui/dist/design-tokens/images/icons/nui-header_heart_size=18.svg';
import heartFilledLarge from '@breuni-ui/nui/dist/design-tokens/images/icons/nui-header_heart_filled_size=24.svg';
import heartLarge from '@breuni-ui/nui/dist/design-tokens/images/icons/nui-header_heart_size=24.svg';
import { customElement, property } from 'lit/decorators.js';
import { WishlistMutationEvent } from '../../../@types/wishlist.js';
import css from './wishlist-button.scss';
import { config } from '../../../scripts/service/config.js';

enum ButtonSize {
  normal,
  large
}

function parseButtonSize(value: string): ButtonSize {
  switch (value) {
    case 'large':
      return ButtonSize.large;
    default:
      return ButtonSize.normal;
  }
}

@customElement('ents-wishlist-button')
export class WishlistButtonElement extends LitElement {
  @property({
    type: Array,
    attribute: 'initial-state',
  }) private initialState: string[] = [];

  @property({ type: Object, attribute: false }) private state: {
    [k: string]: boolean
  } = {};

  @property({ type: Number }) private syan: number = 0;

  @property({ type: String, attribute: 'color-id' }) private colorId?: string = undefined;

  @property({ type: String, attribute: 'article-id' }) private articleId?: string = undefined;

  @property({ type: Boolean, attribute: 'show-text' }) private showText: boolean = false;

  @property({ type: String }) private placement?: string = undefined;

  @property({
    type: String,
    attribute: 'size',
    converter: { fromAttribute: size => parseButtonSize(size ?? '') },
  }) private size: ButtonSize = ButtonSize.normal;

  // use this syntax to make it work in Safari 14
  // working: static get styles() { return css`...` }
  // not working: static styles = css`...`
  static get styles(): CSSResult {
    return unsafeCSS(css);
  }

  private readonly onWishlistAddedEvent: (e: WishlistMutationEvent) => void;

  private readonly onWishlistRemovedEvent: (e: WishlistMutationEvent) => void;

  private selectedSlotNodes?: NodeListOf<Element> = undefined;

  private unselectedSlotNodes?: NodeListOf<Element> = undefined;

  constructor() {
    super();

    this.onWishlistAddedEvent = (e: WishlistMutationEvent) => {
      const data = e.detail;
      this.state = { ...this.state, ...{ [data.identifier]: true } };
    };

    this.onWishlistRemovedEvent = (e: WishlistMutationEvent) => {
      const data = e.detail;
      this.state = { ...this.state, ...{ [data.identifier]: false } };
    };

    this.state = {};
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('ents:wishlist:added', this.onWishlistAddedEvent);
    document.addEventListener('ents:wishlist:removed', this.onWishlistRemovedEvent);

    const initialState = this.initialState !== null ? this.initialState : [];
    this.state = initialState.reduce((agg, v) => ({
      ...agg,
      [v]: true,
    }), {});

    this.selectedSlotNodes = this.querySelectorAll('[slot="selected"]');
    this.unselectedSlotNodes = this.querySelectorAll('[slot="unselected"]');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('ents:wishlist:added', this.onWishlistAddedEvent);
    document.removeEventListener('ents:wishlist:removed', this.onWishlistRemovedEvent);
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);

    this.setAttribute('selected', this.isOnWishlist() ? 'true' : 'false');
  }

  private getIdentifier() {
    return [this.syan, this.colorId].filter(e => !!e).join('-');
  }

  private isOnWishlist() {
    return this.state[this.getIdentifier()];
  }

  private getLabel() {
    if (this.isOnWishlist()) {
      return config.messageWishlistTextActive;
    }
    return config.messageWishlistTextInactive;
  }

  private handleClick(e: Event) {
    e.preventDefault();
    const eventName = this.isOnWishlist() ? 'ents:wishlist:remove' : 'ents:wishlist:add';
    document.dispatchEvent(new CustomEvent(eventName, {
      detail: {
        identifier: this.getIdentifier(),
        syan: this.syan,
        colorId: this.colorId,
        articleId: this.articleId,
        tracking: {
          placement: this.placement,
          context: this.isOnWishlist() ? 'remove_heart' : 'add_heart',
        },
      },
    }));
    e.stopPropagation();
  }

  private hasSlottedContent() {
    if (this.selectedSlotNodes === undefined || this.unselectedSlotNodes === undefined) {
      return false;
    }
    return this.selectedSlotNodes.length > 0 && this.unselectedSlotNodes.length > 0;
  }

  private renderSelectedSlot() {
    this.selectedSlotNodes?.forEach(n => n.removeAttribute('style'));
    return html`
      <slot name="selected"></slot>`;
  }

  private renderUnselectedSlot() {
    this.unselectedSlotNodes?.forEach(n => n.removeAttribute('style'));
    return html`
      <slot name="unselected"></slot>`;
  }

  private renderSlottedContent() {
    return this.isOnWishlist() ? this.renderSelectedSlot() : this.renderUnselectedSlot();
  }

  private renderNativeIcon() {
    if (this.size === ButtonSize.large) {
      return this.isOnWishlist() ? heartFilledLarge() : heartLarge();
    }
    return this.isOnWishlist() ? heartFilledNormal() : heartNormal();
  }

  private renderContent() {
    if (this.hasSlottedContent()) {
      return this.renderSlottedContent();
    }
    return html`${this.renderNativeIcon()}${this.showText ? html`<span class="label">${this.getLabel()}</span>` : ''}`;
  }

  protected render() {
    return html`
      <button @click="${this.handleClick}" type="button" aria-label="${this.getLabel()}"
              data-test-ents-product-on-wishlist="${this.isOnWishlist()}"
              class="${classMap({
    selected: this.isOnWishlist(),
    styled: !this.hasSlottedContent(),
    text: this.showText,
  })}"
      >
        ${this.renderContent()}
      </button>
    `;
  }
}
